import type { ModuleCode } from '../../features/modules/modules';
import type { Role, UserStatus } from '../../schemas';
import apiClient from '../index';

/** The mapping targets. `module` absent = the server's default (O2C). */
export const getCanonicalFields = async (module?: ModuleCode) => {
	const response = await apiClient.get('/reference/canonical-fields', {
		params: module ? { module } : undefined,
	});
	return response.data;
};

/** The modules the product offers: `[{ code, name, description }]`. */
export const getModules = async () => {
	const response = await apiClient.get('/reference/modules');
	return response.data;
};

export const getAnalysisDefinitions = async (module?: ModuleCode) => {
	const response = await apiClient.get('/analyses', {
		params: module ? { module } : undefined,
	});
	return response.data;
};

/** Admin only: the firm's exception-calculation setting per analysis, for
 *  one module (`module` omitted: the server's default, O2C). */
export const getAnalysisSettings = async (module?: ModuleCode) => {
	const response = await apiClient.get('/firm/analysis-settings', {
		params: module ? { module } : undefined,
	});
	return response.data;
};

export const updateAnalysisSetting = async (
	slug: string,
	exceptionCalculationEnabled: boolean,
) => {
	const response = await apiClient.patch(`/firm/analysis-settings/${slug}`, {
		exceptionCalculationEnabled,
	});
	return response.data;
};

/** `module` omitted: the server's default (O2C). */
const moduleQuery = (module?: ModuleCode) =>
	module ? `?${new URLSearchParams({ module })}` : '';

export const getFirmAliases = async (module?: ModuleCode) => {
	const response = await apiClient.get(`/firm/aliases${moduleQuery(module)}`);
	return response.data;
};

export const getFirmRules = async (
	status: 'ACTIVE' | 'DRAFT',
	module?: ModuleCode,
) => {
	const params = new URLSearchParams({ status });
	if (module) params.set('module', module);
	const response = await apiClient.get(
		`/firm/classification-rules?${params}`,
	);
	return response.data;
};

export const getRulesets = async (module?: ModuleCode) => {
	const response = await apiClient.get(
		`/firm/rulesets${moduleQuery(module)}`,
	);
	return response.data;
};

export const publishRuleset = async (rulesetId: string) => {
	const response = await apiClient.post<void>(
		`/firm/rulesets/${rulesetId}/publish`,
	);
	return response.data;
};

export const deleteRule = async (ruleId: string) => {
	const response = await apiClient.delete<void>(
		`/firm/classification-rules/${ruleId}`,
	);
	return response.data;
};

/** A new rule goes into `module`'s draft ruleset (omitted: the server's
 *  default, O2C). An edit follows its rule, so `updateRule` takes none. */
export const createRule = async (body: object, module?: ModuleCode) => {
	const response = await apiClient.post<unknown>(
		'/firm/classification-rules',
		{
			...body,
			...(module ? { module } : {}),
		},
	);
	return response.data;
};

export const updateRule = async (ruleId: string, body: unknown) => {
	const response = await apiClient.patch<unknown>(
		`/firm/classification-rules/${ruleId}`,
		body,
	);
	return response.data;
};

export const confirmMapping = async (fileId: string, body: unknown) => {
	const response = await apiClient.post<void>(
		`/files/${fileId}/mapping/confirm`,
		body,
	);
	return response.data;
};

export const getMembers = async () => {
	const response = await apiClient.get('/users');
	return response.data;
};

export const getInvites = async () => {
	const response = await apiClient.get('/auth/invites');
	return response.data;
};

export const updateUser = async (
	userId: string,
	role?: Role,
	status?: UserStatus,
) => {
	const response = await apiClient.patch<void>(`/users/${userId}`, {
		...(role ? { role } : {}),
		...(status ? { status } : {}),
	});
	return response.data;
};

export const revokeInvite = async (inviteId: string) => {
	const response = await apiClient.delete<void>(`/auth/invites/${inviteId}`);
	return response.data;
};

/** An administrator adds a colleague directly, with a first password. */
export const createUser = async (body: {
	name: string;
	email: string;
	password: string;
	role: Role;
}) => {
	const response = await apiClient.post('/users', body);
	return response.data;
};

export const createInvite = async (body: { email: string; role: Role }) => {
	const response = await apiClient.post('/auth/invites', body);
	return response.data;
};

export const getApprovals = async () => {
	const response = await apiClient.get('/approvals');
	return response.data;
};

export const getActivity = async () => {
	const response = await apiClient.get('/activity');
	return response.data;
};

/** A new client is in every module. */
export const createClient = async (body: unknown) => {
	const response = await apiClient.post('/clients', body);
	return response.data;
};

export const getClientLayouts = async (clientId: string) => {
	const response = await apiClient.get(`/clients/${clientId}/layouts`);
	return response.data;
};

export const getMapping = async (path: string) => {
	const response = await apiClient.get(path);
	return response.data;
};
